import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from 'react-router-dom';
import { Package, MessageSquare, FileText, Truck, ArrowRight, DollarSign, Clock } from 'lucide-react';

export default function SupplierDashboard() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* KPI Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-slate-500">Open Invitations</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">12</div>
            <div className="text-xs text-amber-600 mt-1">Deadline: 3 days</div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-5 h-5 text-[#7A4020]" />
              <span className="text-sm text-slate-500">Quotes Submitted</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">68</div>
            <div className="text-xs text-slate-500 mt-1">52 approved</div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-5 h-5 text-[#7A4020]" />
              <span className="text-sm text-slate-500">Active POs</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">3</div>
            <div className="text-xs text-slate-500 mt-1">In fulfilment</div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Truck className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-slate-500">Pilot C1 Dispatched</span>
            </div>
            <div className="text-2xl font-bold text-emerald-600">300</div>
            <div className="text-xs text-emerald-600 mt-1">✓ Complete</div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-5 h-5 text-[#7A4020]" />
              <span className="text-sm text-slate-500">Revenue Est.</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">$78,246</div>
            <div className="text-xs text-slate-500 mt-1">CDS Cycle 1 quotes</div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Invitations */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Pending Invitations — Respond Now
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 border rounded-lg hover:shadow-sm transition-shadow">
              <div>
                <p className="font-medium">"The Wonders of Coding"</p>
                <p className="text-sm text-slate-500">Budget ceiling: $20.00 | Deadline: 2 days</p>
              </div>
              <Button asChild size="sm" className="bg-[#7A4020] hover:bg-[#5A3018]">
                <Link to="/supplier/quote-response">Respond Now</Link>
              </Button>
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg hover:shadow-sm transition-shadow border-amber-200 bg-amber-50">
              <div>
                <p className="font-medium">"Her Story: Women in Sg"</p>
                <p className="text-sm text-amber-600">Budget ceiling: $22.00 | Deadline: 1 day ⚠️</p>
              </div>
              <Button asChild size="sm" className="bg-[#7A4020] hover:bg-[#5A3018]">
                <Link to="/supplier/quote-response">Respond Now</Link>
              </Button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">+ 10 more pending invitations</p>
        </CardContent>
      </Card>

      {/* Active POs */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Active Purchase Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">PO-SUP-OT-CDS-C1-2028</p>
                  <p className="text-sm text-slate-500">4,820 books | 38 titles | Dispatch: Oct 2027</p>
                </div>
                <Badge className="bg-slate-100 text-slate-700">📅 Upcoming</Badge>
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">PO-SUP-OT-MK-2026</p>
                  <p className="text-sm text-slate-500">360 books | 20 MK titles | Dispatch: Sep 2026</p>
                </div>
                <Badge className="bg-blue-100 text-blue-700">🚚 In Fulfilment</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Quote Response</h3>
                <p className="text-sm text-slate-500">Respond to open invitations</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/supplier/quote-response">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">PO Fulfilment</h3>
                <p className="text-sm text-slate-500">Manage order dispatch</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/supplier/po-fulfilment">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Order History</h3>
                <p className="text-sm text-slate-500">Past orders & analytics</p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/supplier/order-history">
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
